import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AmorgAdminToken, Article, ArticleContact, ArticleList, ArticleSubscriber, ArticleType, rng } from '../../../amorg-common';
import { getActiveUser } from '../../../api/adminClientApi';
import { articlesClientApi } from '../../../api/articlesClientApi';
import { ImportanceAvatar } from '../../../designSystem/avatar/ImportanceAvatar';
import { Button } from '../../../designSystem/button/Button';
import { LinkButton } from '../../../designSystem/button/LinkButton';
import { colors } from '../../../designSystem/colors/color';
import { ConfirmationModal } from '../../../designSystem/compositeComponents/ConfirmationButton';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { ListRow, ListRowWithChildren } from '../../../designSystem/compositeComponents/ListRow';
import { OnOff } from '../../../designSystem/inputs/OnOff';
import { Label } from '../../../designSystem/Label';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { toDateTimeString } from '../../../utils/date';
import { AdminTab } from '../_components/AdminTab';
import { AmorgAdminPageProvider } from '../AmorgAdminPageContext';
import { ArticlesTab } from './_components/ArticlesTab';
import { ArticleViewAppendixPanel } from './_components/articleView/ArticleViewAppendixPanel';
import { ArticleViewFields } from './_components/articleView/ArticleViewFields';
import { ArticleViewNotesPanel } from './_components/articleView/ArticleViewNotesPanel';
import { ArticleViewReleasePanel } from './_components/articleView/ArticleViewReleasePanel';
import { ArticleViewSubscriberPanel } from './_components/articleView/ArticleViewSubscriberPanel';

export default function ArticlesView (): JSX.Element 
{
    const params = useParams<{ id: string; }>();

    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken>(null);
    const [lists, setLists] = React.useState<ArticleList[]>([]);
    const [types, setTypes] = React.useState<ArticleType[]>([]);
    const [initialLoad, setInitialLoad] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const [articleContacts, setArticleContacts] = React.useState<ArticleContact[]>([]);
    const [articles, setArticles] = React.useState<Article[]>([]);
    const [articleIndex, setArticleIndex] = React.useState(0);
    const [subscribers, setSubscribers] = React.useState<ArticleSubscriber[]>([]);

    const navigate = useNavigate();

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            setLists(await articlesClientApi.getLists());
            setTypes(await articlesClientApi.getTypes());
            const arts = await articlesClientApi.getArticleVersions(params.id);
            setArticles(arts.versions);
            setSubscribers(arts.subscribers);
            const index = 0;
            setArticleIndex(index);
            const contacts = await articlesClientApi.getContacts(undefined, arts.versions[0].articleId);
            setArticleContacts(contacts);

            setActiveUser(await getActiveUser(() => navigate('/amorgy/login')));

            setInitialLoad(true);
            setLoading(false);
        };
        getData();
    }, []);

    async function handleClickCreateVersion ()
    {
        setLoading(true);

        await articlesClientApi.createArticleVersion(params.id, rng.getRandomId());
        const arts = await articlesClientApi.getArticleVersions(params.id);
        setArticles(arts.versions);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    async function handleClickReleaseVersion (silentRelease:boolean)
    {
        setLoading(true);

        await articlesClientApi.releaseArticleVersion(params.id, articles[articleIndex].version, silentRelease);
        const arts = await articlesClientApi.getArticleVersions(params.id);
        setArticles(arts.versions);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    async function handleClickMergeUnreleasedVersion ()
    {
        setLoading(true);

        await articlesClientApi.mergeUnreleasedArticleVersion(params.id, articles[articleIndex].version);
        const arts = await articlesClientApi.getArticleVersions(params.id);
        setArticles(arts.versions);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    async function handleReload ()
    {
        setLoading(true);
        const arts = await articlesClientApi.getArticleVersions(params.id);
        setArticles(arts.versions);
        const index = 0;
        setArticleIndex(index);

        setLoading(false);
    }

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout
                noDivider
                headerContainer={
                    <>
                        <AdminTab selectedTab='articles' />
                        <ArticlesTab selectedTab='articles' />
                    </>
                }

                contentContainer={

                    <Flex flexDirection='column' gap={16} marginBotttom={40}>
                
                        {initialLoad &&
                            <>
                                <Flex>
                                    <LinkButton variant='text' text='Tilbage til oversigten' href='/amorgy/articles' />

                                    <div style={{ flexGrow: 1, gap: '8px', justifyContent: 'flex-end', display: 'flex' }}>

                                        <Button variant='filled' disabled={articles[articleIndex].release === null} text='Opret ny version' onClick={handleClickCreateVersion} />

                                        <ReleaseModalButton
                                            articleAssessment={articles[articleIndex].assessment}
                                            articleDescription={articles[articleIndex].changeDescription}
                                            articleVersion={articles[articleIndex].version}
                                            disabled={articles[articleIndex].release !== null}
                                            onClickRelease={handleClickReleaseVersion}
                                        />

                                        <MergeModalButton
                                            articleVersion={articles[articleIndex].version}
                                            disabled={articles[articleIndex].release !== null || articles[articleIndex].version < 2}
                                            onClickMerge={handleClickMergeUnreleasedVersion}
                                        />

                                    </div>
                                </Flex>

                                <Flex gap={48} flexDirection='column'>
                                    {articles.map((a, i) =>
                                    {
                                        const oldVersion = i < articles.length - 1 ? articles[i + 1] : null;
                                        const newVersion = articles[i];

                                        return (
                                            <div key={`${a.articleVersionId}`}>

                                                <ListRowWithChildren level='level1'
                                                    styleVariant='neutral'
                                                    stripColor={a.release === null ? colors.icon.warning : colors.icon.active}
                                                    expanded={true}
                                                    onCollapse={() => { return; }}
                                                    content={
                                                        <Flex flexDirection='row' gap={8} alignItems='center'>
                                                            <ImportanceAvatar importance={a.importance} />
                                                            <Label>Version {a.version}</Label>
                                                            {a.release !== null
                                                                ? <small>Udgivet d. {toDateTimeString(a.release.releasedDate)}</small>
                                                                : <small>Ikke udgivet</small>
                                                            }
                                                        </Flex>
                                                    }
                                                    childRows={
                                                        <section>

                                                            <ListRow level='level2' styleVariant='neutral'>
                                                                <ArticleViewSubscriberPanel subscribers={subscribers} />
                                                            </ListRow>

                                                            <ListRow level='level2' styleVariant='neutral'>
                                                                <ArticleViewFields types={types} lists={lists}
                                                                    oldVersion={oldVersion}
                                                                    newVersion={newVersion}
                                                                    latestVersion={i === 0}
                                                                    onChanged={handleReload}
                                                                />
                                                            </ListRow>

                                                            <ListRow level='level2' styleVariant='neutral'>
                                                                {initialLoad &&
                                                                    <ArticleViewNotesPanel articleVersionId={a.articleVersionId}
                                                                        activeUser={activeUser}
                                                                        notes={a.notes}
                                                                        onChanged={handleReload} />
                                                                }
                                                            </ListRow>

                                                            <ListRow level='level2' styleVariant='neutral'>
                                                                {initialLoad &&
                                                                    <ArticleViewReleasePanel
                                                                        article={a}
                                                                        contacts={articleContacts}
                                                                        releaseRecords={a.releaseRecords}
                                                                        onChanged={handleReload} />
                                                                }
                                                            </ListRow>

                                                            {initialLoad &&
                                                                <ListRow level='level2' styleVariant='neutral'>
                                                                    <ArticleViewAppendixPanel
                                                                        articleVersionId={a.articleVersionId}
                                                                        types={types}
                                                                        lists={lists}
                                                                        appendices={a.appendices}
                                                                        canEdit={a.release === null}
                                                                        onChanged={handleReload} />
                                                                </ListRow>
                                                            }

                                                        </section>
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </Flex>
                            </>
                        }
                    </Flex>
                } />

        </AmorgAdminPageProvider>
    );
}

type ReleaseModalButtonProps = {
    articleVersion:number,
    articleDescription:string,
    articleAssessment:string,
    disabled?:boolean,
    onClickRelease:(silentRelease:boolean) => void
}
function ReleaseModalButton(props:ReleaseModalButtonProps):JSX.Element
{
    const [showModal, setShowModal] = React.useState(false);
    const [sendNotifications, setSendNotifications] = React.useState(true);

    return (
        <>
            <Button
                variant="filled"
                onClick={() => {
                    setShowModal(true);
                    setSendNotifications(true);
                }}
                text="Udgiv version"
                disabled={props.disabled} 
            />
                        
            <ConfirmationModal 
                showModal={showModal}
                buttonText="Udgiv"
                title="Udgiv nyeste version"
                content={
                    <Flex flexDirection='column' gap={16}>
                        <div>Er du sikker på at du vil udgive version {props.articleVersion} ?</div>
                        {(props.articleDescription.trim() === '' || props.articleAssessment.trim() === '') &&
                            <div style={{ color: colors.icon.error }}>
                                <span><b>*BEMÆRK!*</b></span><br />
                                <span>Ændringsbeskrivelse og/eller vurdering er ikke udfyldt!!</span>
                            </div>
                        }
                    <Flex>
                        <OnOff active={sendNotifications} onClick={(active) => setSendNotifications(active)} />
                        <span style={{ marginLeft: 8 }}>Send notifikationer</span>
                    </Flex>
                </Flex>}
                onCancel={() => setShowModal(false)}
                onConfirm={() => {
                    props.onClickRelease(!sendNotifications)
                    setShowModal(false);
                }}
                requireKey
            />
        </>
    );
}

type MergeModalButtonProps = {
    articleVersion:number,
    disabled?:boolean,
    onClickMerge:() => void
}
function MergeModalButton(props:MergeModalButtonProps):JSX.Element
{
    const [showModal, setShowModal] = React.useState(false);

    return (
        <>
            <Button
                variant='outlined'
                onClick={() => setShowModal(true)}
                text='Flet med forrige version'
                disabled={props.disabled}
            />

            <ConfirmationModal
                showModal={showModal}
                buttonText='Flet version'
                title='Overskriv forrige version'
                content={
                    <Flex flexDirection='column' gap={16}>
                        <div>Er du sikker på at du vil overskrive version {props.articleVersion -1} med version {props.articleVersion}?</div>
                        <div>Version {props.articleVersion} slettes efter fletningen, og abonnenter vil ikke blive informeret om ændringerne.</div>
                    </Flex>
                }
                onCancel={() => setShowModal(false)}
                onConfirm={() => {
                    props.onClickMerge();
                    setShowModal(false);
                }}
                requireKey
            />
        </>
    );
}
